import React, { useState } from "react";
import {
  Badge,
  BadgeProps,
  Box,
  Loader,
  Menu,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { IconChevronDown } from "@tabler/icons-react";
import {
  AppointmentStatus,
  SELECTABLE_STATUSES,
  getAppointmentStatusColor,
} from "../../types/calendar";
import styles from "./AppointmentStatusBadgeMenu.module.css";

export interface AppointmentStatusBadgeMenuProps {
  /** The appointment's current status (label + starting point for transitions). */
  currentStatus?: string | null;
  /** Persist the picked status. Applied immediately (no confirm dialog). */
  onChange: (next: AppointmentStatus) => void | Promise<void>;
  /** Render a plain (non-interactive) badge, e.g. when the user can't edit. */
  disabled?: boolean;
  /** External saving state (OR-ed with the component's own in-flight state). */
  loading?: boolean;
  size?: BadgeProps["size"];
  radius?: BadgeProps["radius"];
  className?: string;
  /** Uppercase the label (the calendar pop-over does). */
  uppercaseLabel?: boolean;
  /** Show the small status dot before the label (the transaction modal does). */
  withDot?: boolean;
  /**
   * Why a status can't be picked right now (shown as a tooltip on that option,
   * which stays visible but disabled), or null when it can. Evaluated each
   * time the menu opens, so time-based gates (Arrived) stay current.
   */
  getStatusBlockedReason?: (status: AppointmentStatus) => string | null;
  /**
   * How the badge itself renders (label + Mantine color), e.g. the
   * display-only "Accepted (Unconfirmed)" from `appointmentStatusDisplay`.
   * Defaults to the status and its color. Menu items always list the real
   * statuses.
   */
  display?: { label: string; color: string } | null;
}

/**
 * The appointment-status badge, upgraded to a dropdown: click to move the
 * appointment to another status. Shared by the transaction modal and the
 * calendar pop-over. Every pick applies immediately — the client-SMS side
 * effects are decided server-side by the transition, not here.
 *
 * Degrades to a plain, static badge when the status is the terminal `Completed`
 * (locked, with a tooltip), when `disabled`, or when there is no status yet — so
 * it is a drop-in replacement for the old status badge.
 */
export default function AppointmentStatusBadgeMenu({
  currentStatus,
  onChange,
  disabled = false,
  loading = false,
  size = "md",
  radius,
  className,
  uppercaseLabel = false,
  withDot = false,
  getStatusBlockedReason,
  display,
}: AppointmentStatusBadgeMenuProps) {
  const [saving, setSaving] = useState(false);
  // Bumped on open so blocked reasons are recomputed against the current time.
  const [, setOpenCount] = useState(0);

  const status = currentStatus || undefined;
  const color = display?.color ?? getAppointmentStatusColor(status);
  const label = display?.label ?? status ?? "";
  const isSaving = saving || loading;

  const interactive = !!status && status !== "Completed" && !disabled;

  const rightSection = isSaving ? (
    <Loader size={12} color={color} />
  ) : interactive ? (
    <IconChevronDown size={14} stroke={2} />
  ) : undefined;

  const badge = (
    <Badge
      variant="light"
      color={color}
      size={size}
      radius={radius}
      className={className}
      leftSection={
        withDot ? (
          <Box w={6} h={6} bg="currentColor" style={{ borderRadius: 999 }} />
        ) : undefined
      }
      rightSection={rightSection}
      style={interactive ? { pointerEvents: "none" } : undefined}
    >
      {uppercaseLabel ? label.toUpperCase() : label}
    </Badge>
  );

  // Non-interactive: a plain badge (Completed / disabled / no status).
  if (!interactive) {
    if (status === "Completed") {
      return (
        <Tooltip
          label="Completed is set at checkout and can't be changed here"
          withArrow
        >
          {badge}
        </Tooltip>
      );
    }
    return badge;
  }

  const handleSelect = async (next: AppointmentStatus) => {
    if (next === status) return;
    setSaving(true);
    try {
      await onChange(next);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Menu
      position="bottom-start"
      width={210}
      radius="md"
      shadow="lg"
      withinPortal
      disabled={isSaving}
      onOpen={() => setOpenCount((n) => n + 1)}
    >
      <Menu.Target>
        <UnstyledButton
          className={styles.trigger}
          aria-label={`Appointment status: ${label}. Change appointment status`}
          disabled={isSaving}
        >
          {badge}
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Change status</Menu.Label>
        {SELECTABLE_STATUSES.map((s) => {
          const blockedReason =
            s === status ? null : (getStatusBlockedReason?.(s) ?? null);
          const item = (
            <Menu.Item
              key={s}
              disabled={s === status}
              // data-disabled (not disabled) keeps the item hoverable so the
              // tooltip explaining why can show; Mantine skips it on click.
              data-disabled={blockedReason ? true : undefined}
              aria-disabled={blockedReason ? true : undefined}
              onClick={() => {
                if (!blockedReason) void handleSelect(s);
              }}
              leftSection={
                <Box
                  w={8}
                  h={8}
                  bg={getAppointmentStatusColor(s)}
                  style={{ borderRadius: 999 }}
                />
              }
            >
              {s}
            </Menu.Item>
          );
          return blockedReason ? (
            <Tooltip
              key={s}
              label={blockedReason}
              position="right"
              withArrow
              multiline
              w={220}
              events={{ hover: true, focus: true, touch: true }}
            >
              {item}
            </Tooltip>
          ) : (
            item
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
}
